import { useGetCurrentUserQuery } from '@carboncopyco/data';
import {
  type DemoOverlay,
  type IDemo,
  type IPresentation,
  type IUser,
  PresentationType,
  RecordingTypes,
} from '@carboncopyco/types';
import {
  SettingMainItemsKeys,
  SettingsSubKeys,
} from '@carboncopyco/types/desktop/appSettings';
import { useEffect, useRef } from 'react';
import api from '../../../shared/services/analytics-api';
import { generateGroupId, getGroupDefaultValue } from '../../../util/tab-util';
import { preparePresentationsData } from '../../shared/utils/preparePresentationsData';
import { useUser } from '../../store/context';
import { useTabManager } from '../TabManager';

import {
  type IGlobalTab,
  PresentationTypeConst,
  type SpeakerPanelData,
  type TabGroup,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';

type OpenPresentationTabArgs = {
  presentation: IPresentation;
  demo: IDemo;
  forClient?: string;
  type?: TabType;
  group?: TabGroup;
  analyticsSessionId: string;
};

export const usePresentationService = () => {
  const { createGlobalTab } = useTabManager();
  const { data: userData } = useGetCurrentUserQuery();
  const { activeOrganization } = useUser();
  const userRef = useRef<{ user: IUser }>();

  useEffect(() => {
    if (!userRef.current && userData) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      userRef.current = userData;
    }
  }, [userData]);

  const identifyAnalytics = (globalTab: IGlobalTab) => {
    const presentation = globalTab.payload?.presentation;
    const identifyData = {
      is_iframe: false,
      organization_id:
        activeOrganization?.id?.toString() ||
        globalTab.payload.organizationId?.toString(),
      presentation_id:
        presentation.id?.toString() || presentation.presentationId,
      revision_id:
        presentation.revisionId || globalTab?.payload?.demo?.revisionId,
      title: presentation.presentationName || globalTab.title || '',
      user: {
        name: userRef?.current?.user?.name || '',
        email: userRef?.current?.user?.email || '',
        role: 'presenter',
      },
    };

    return api.event.identify(identifyData, PresentationType.liveDemo);
  };

  const sendAnalytics = async (
    globalTab: IGlobalTab,
    url: string,
    sessionId: string
  ) => {
    try {
      const presentation = globalTab.payload?.presentation;
      const urlObj = new URL(url);
      if (!presentation) return;
      const visitData = {
        domain: urlObj.hostname,
        title: presentation.presentationName || globalTab.title || '',
        organization_id: globalTab.payload.organizationId?.toString() || '',
        presentation_id: presentation?.id?.toString(),
        revision_id:
          presentation?.revisionId?.toString() ||
          globalTab.payload?.demo?.revisionId,
        event_type: 'visit',
        url: encodeURIComponent(url),
        path: urlObj.pathname,
        session_id: sessionId,
      };
      await api.event.visit(visitData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error sending analytics', e);
    }
  };

  const openPresentationTab = async ({
    presentation,
    demo,
    forClient,
    type = TabType.PRESENT_DEMO,
    group,
    analyticsSessionId,
  }: OpenPresentationTabArgs) => {
    const tabTitle = demo.demoName;

    const url = decodeURIComponent(demo.firstUrl);
    createGlobalTab({
      cloneId: demo.id.toString(),
      workspaceId: demo?.workspaceId,
      analyticsSessionId,
      presentationId: String(presentation.id),
      presentationType: PresentationTypeConst.demo,
      payload: {
        demo,
        forClient: forClient || '',
        presentation,
        organizationId:
          activeOrganization?.id?.toString() || presentation.organization,
        hidden: presentation.hidden,
        showWithoutDrafts: true,
      },
      title: tabTitle,
      hoverTitle: `${demo.demoName} for ${forClient}`,
      url,
      type,
      group,
    });
  };

  const openDemoPresentation = async (
    presentation: IPresentation,
    demo: IDemo | DemoOverlay,
    analyticsSessionId: string
  ) => {
    const preparedData = preparePresentationsData({
      presentations: [presentation],
      demosList: [demo],
      analyticsSessionId,
    });
    const { presentation: preparedPresentation, demo: preparedDemo } =
      preparedData[0];
    const groupId = preparedPresentation?.id
      ? generateGroupId(
          Number(preparedPresentation?.id),
          TabGroupName.DemoPresent
        )
      : undefined;

    if (groupId) {
      const appSettings =
        await window?.dsDesktop?.appSettings?.getAppSettings();
      const appSettingsIsMouseHighlightActive =
        appSettings?.[SettingMainItemsKeys.MouseAnimation][
          SettingsSubKeys.MouseAnimationToggleState
        ];
      const appSettingsIsOpenByDefault =
        appSettings?.[SettingMainItemsKeys.PresentationDisplay][
          SettingsSubKeys.ShowSpeakerPanelByDefault
        ];

      const initialSpeakerPanelData: SpeakerPanelData = {
        currentTime: 0,
        isNotesOpen: false,
        isWindowOpen: false,
        isOpen: appSettingsIsOpenByDefault ?? false,
        activePresentationIndex: 0,
        demosInfo: [
          {
            presentationName: preparedPresentation?.presentationName || '',
            image: demo.image,
            notes: preparedPresentation?.notes || '',
            id: demo.id,
            screenshotImageType:
              demo.recordingType === RecordingTypes.overlay
                ? 'overlay'
                : 'demo',
            canDetach: true,
          },
        ],
        isMouseHighlightActive: appSettingsIsMouseHighlightActive ?? true,
      };

      await window?.dsDesktop?.speakerPanel.setData(
        groupId,
        initialSpeakerPanelData
      );
    }

    if (preparedPresentation && preparedDemo) {
      await openPresentationTab({
        analyticsSessionId,
        presentation: preparedPresentation,
        demo: {
          ...preparedDemo,
          playerToken:
            preparedPresentation.playerToken || preparedDemo.playerToken,
          firstUrl: preparedPresentation?.firstUrl || preparedDemo.firstUrl,
        } as IDemo,
        forClient: preparedPresentation.forClient,
        type: TabType.PRESENT_DEMO,
        group: groupId
          ? getGroupDefaultValue(
              groupId,
              TabGroupName.DemoPresent,
              preparedDemo.id
            )
          : undefined,
      });
    }
  };

  return {
    openPresentationTab,
    identifyAnalytics,
    sendAnalytics,
    openDemoPresentation,
  };
};
